/**
 * Responsive image component with lazy loading
 * Provides automatic srcset generation, lazy loading, and responsive breakpoints
 */

import { globalState } from '@/components/helpers/utils'
import { ComponentProps } from 'preact'
import { useEffect, useState } from 'preact/hooks'

interface PreactImageProps extends ComponentProps<'image'> {
  imageUrl: string[] | string
  alt: string
  className: string
  imageClass: string
  isLazy: boolean
  isCover: boolean
  imageSrcSet: string[]
  imageBreakpoints: string;
  imageSizes: number[]
  sizesAttr?: string
}

/**
 * Adds/replaces the Shopify CDN `width=` param. Without it every srcset candidate points
 * at the same untransformed master, so a 120px thumbnail decodes the full-resolution
 * original no matter which candidate the browser picks.
 */
function withWidth(url: string, width: number): string {
  if (!url) return url
  if (!url.includes('cdn.shopify.com') && !url.startsWith('//') && !url.startsWith('/')) return url

  try {
    const parsed = new URL(url, window.location.origin)
    parsed.searchParams.set('width', String(width))
    return url.startsWith('//') ? `//${parsed.host}${parsed.pathname}${parsed.search}` : parsed.toString()
  } catch {
    return url
  }
}

function PreactImage({
  imageUrl = globalState.shopify.defaultImage,
  alt,
  className,
  imageClass,
  isLazy = true,
  isCover = true,
  imageSrcSet = [],
  imageBreakpoints = '(min-width: 0px)',
  imageSizes = [150],
  sizesAttr
}: PreactImageProps) {
  const [currentImageUrls] = useState(imageUrl)
  const classImage = `${isCover ? 'object-cover' : 'object-contain'} ${isLazy && 'lazy'} ${imageClass}`
  const customClass = `relative ${className}`
  let fallbackImageUrl = currentImageUrls[0]
  let breakpoints = imageBreakpoints.includes('|') ? imageBreakpoints.split('|') : [imageBreakpoints]

  if (currentImageUrls.length <= 1) {
    breakpoints = breakpoints.splice(0, 1)
  } else {
    breakpoints = breakpoints.splice(0, currentImageUrls.length)
    fallbackImageUrl = currentImageUrls[currentImageUrls.length - 1]
  }

  const sizes = sizesAttr ?? imageSizes.map((curr, index) => `${breakpoints[index]} ${curr}vw`).join(',')

  useEffect(() => {
    if (window.lazyLoadInstance) {
      window.lazyLoadInstance.update()
    }
  }, [])

  const getSrcSet = (url: string) => {
    if (imageSrcSet.length === 0) return url
    return imageSrcSet.map((srcset) => `${withWidth(url, Number(srcset))} ${srcset}w`).join(', ')
  }

  const largestSrcSetWidth = imageSrcSet.length > 0
    ? Math.max(...imageSrcSet.map(Number).filter((width) => !isNaN(width)))
    : 0

  return (
    <picture className={customClass}>
      {breakpoints.map((breakpoint, index) => (
        <source className="!size-0" media={breakpoint} data-srcset={getSrcSet(currentImageUrls[index])} data-sizes={sizes} />
      ))}
      <img
        src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAAAXNSR0IArs4c6QAAAA1JREFUCB1j+Pjx438ACX0D04PrXz0AAAAASUVORK5CYII="
        data-src={largestSrcSetWidth > 0 ? withWidth(fallbackImageUrl, largestSrcSetWidth) : fallbackImageUrl}
        data-srcset={getSrcSet(fallbackImageUrl)}
        data-sizes={sizes}
        className={classImage}
        alt={alt || 'Image'}
      />
    </picture>
  )
}

export default PreactImage
